import fambService from '@/services/toolbox/famb-service';
import { cancelController } from '@/services/api-client';

import { FAMB_SETTINGS_REPONSES } from './constants/responses';

export const defaultFambSettings = {
	enableProfileInd: null,
	contactProfileInd: null,
	blinkIntegrationInd: null,
	fambSpecialization: [],
	profilePath: null,
	profileSlug: null,
	urlValidationCancelSource: null,
};

const state = {
	settings: { ...defaultFambSettings },
	error: null,
	errorType: null,
	isLoading: false,
	isSaving: false,
	urlValidationCancelSource: null,
};

const mutations = {
	RESET_FAMB_STATE(state) {
		state.settings = { ...defaultFambSettings };
		state.error = null;
		state.errorType = null;
		state.isLoading = false;
		state.isSaving = false;
	},
	CLEAR_INPUT_ERRORS(state) {
		state.error = null;
		state.errorType = null;
	},
	LOADING_FAMB_SETTINGS(state, payload) {
		state.isLoading = payload;
		if (payload === true) {
			state.error = null;
			state.errorType = null;
		}
	},
	GET_FAMB_SETTINGS_SUCCESS(state, payload) {
		state.settings = {
			enableProfileInd: payload.enableProfileInd,
			contactProfileInd: payload.contactProfileInd,
			blinkIntegrationInd: payload.blinkIntegrationInd,
			fambSpecialization: payload.fambSpecialization,
			profilePath: payload.profilePath,
			profileSlug: payload.profileSlug,
		};
		state.error = null;
		state.errorType = null;
	},
	SAVING_FAMB_SETTINGS(state, payload) {
		state.isSaving = payload;
		if (payload === true) {
			state.error = null;
			state.errorType = null;
		}
	},
	SAVE_FAMB_SETTINGS_SUCCESS(state, payload) {
		const { fambYourProfileURL, fambYourProfileURLSlug, ...fambSettings } =
			payload;
		state.settings = { ...fambSettings };
	},
	FAMB_SETTINGS_API_ERROR(state, type) {
		state.error = FAMB_SETTINGS_REPONSES[type];
		state.errorType = type;
		state.settings = { ...defaultFambSettings };
	},
	FAMB_SETTINGS_ERROR(state, type) {
		state.error = FAMB_SETTINGS_REPONSES[type];
		state.errorType = type;
	},
	SET_URL_VALIDATION_CANCEL_SOURCE(state, cancelTokenSource) {
		state.urlValidationCancelSource = cancelTokenSource;
	},
	SET_BLINK_INTEGRATION_IND(state) {
		state.settings.blinkIntegrationInd = false;
	},
};

const actions = {
	async getFambSettings({ commit, dispatch, state, rootState }) {
		dispatch('cancelProfileURLValidityCheck');
		try {
			const { userId } = rootState.profile;
			commit('LOADING_FAMB_SETTINGS', true);
			const response = await fambService.getFambSettings(userId);
			if (response && response.data) {
				commit('GET_FAMB_SETTINGS_SUCCESS', response.data);
			} else {
				// data not recieved
				commit('FAMB_SETTINGS_ERROR', 'GET_SETTINGS_ERROR');
			}
		} catch (error) {
			commit('FAMB_SETTINGS_ERROR', 'GET_SETTINGS_ERROR');
		} finally {
			commit('LOADING_FAMB_SETTINGS', false);
		}
	},
	async saveFambSettings({ commit, dispatch, state, rootState }, settings) {
		dispatch('cancelProfileURLValidityCheck');
		try {
			const {
				blinkLink,
				contactId,
				preferredName,
				rolodexContactId,
				userId,
				fambYourProfileURL,
			} = rootState.profile;
			const { rolodexEntityId } = rootState.companyProfile;

			const updatedSettings = {
				...settings,
				userId,
				contactId,
				rolodexContactId,
				rolodexEntityId,
				blinkURL: blinkLink,
				modifiedUserName: preferredName,
			};
			commit('SAVING_FAMB_SETTINGS', true);
			const response = await fambService.saveFambSettings(updatedSettings);
			switch (response.data) {
				case 1:
					//    1 = Success
					// NOTE: user-profile also listens for this mutation
					commit('SAVE_FAMB_SETTINGS_SUCCESS', settings);
					break;
				case -1:
					//    -1 = FambURL error (already exists)
					commit('FAMB_SETTINGS_ERROR', 'URL_NOT_UNIQUE_ERROR');
					break;
				case -2:
					//   -2 = Page did not save, other issues
					commit('FAMB_SETTINGS_API_ERROR', 'SAVE_SETTINGS_ERROR');
					break;
				default:
					commit('FAMB_SETTINGS_API_ERROR', 'SAVE_SETTINGS_ERROR');
			}
		} catch (error) {
			commit('FAMB_SETTINGS_API_ERROR', 'SAVE_SETTINGS_ERROR');
		} finally {
			commit('SAVING_FAMB_SETTINGS', false);
		}
	},
	resetFambState({ commit }) {
		commit('RESET_FAMB_STATE');
	},
	getCustomProfile({ commit }) {
		commit('GET_CUSTOM_PROFILE', { profileSlug, profilePath });
	},
	async checkProfileURLValidity(
		{ commit, dispatch, rootState, state },
		profileSlug,
	) {
		let signal;
		commit('CLEAR_INPUT_ERRORS');
		if (profileSlug.length > 2) {
			try {
				const { userId } = rootState.profile;

				// The "controller" object is used to cancel an api request
				const controller = cancelController();
				signal = controller.signal;

				if (state.urlValidationCancelSource) {
					dispatch('cancelProfileURLValidityCheck');
				}
				commit('SET_URL_VALIDATION_CANCEL_SOURCE', controller);

				const { data } = await fambService.checkProfileURLValidity({
					profileSlug,
					signal,
					userId,
				});

				if (signal.aborted === true) {
					throw new Error('api call was aborted');
				}

				// true means the URL is not unique
				if (data === true) {
					commit('URL_NOT_UNIQUE_ERROR');
					commit('FAMB_SETTINGS_ERROR');
				}
				commit('SET_URL_VALIDATION_CANCEL_SOURCE', null);
			} catch (error) {
				/* "Cancelled" errors aren't true errors since they are intentional and should
				just be ignored. Other errors api errors don't result in any changes to the
				UI but we still need to remove the old Cancel Source */
				if (!signal.aborted) {
					commit('SET_URL_VALIDATION_CANCEL_SOURCE', null);
				}
			}
		} else {
			dispatch('cancelProfileURLValidityCheck');
		}
	},
	cancelProfileURLValidityCheck({ commit, state }) {
		if (state.urlValidationCancelSource) {
			state.urlValidationCancelSource.abort();
			commit('SET_URL_VALIDATION_CANCEL_SOURCE', null);
		}
	},
	clearInputErrors({ commit }) {
		commit('CLEAR_INPUT_ERRORS');
	},
	setBlinkIntegrationInd({ commit }) {
		commit('SET_BLINK_INTEGRATION_IND');
	},
};

export default {
	state,
	mutations,
	actions,
};
